// The broker owns the protocol endpoints and this app owns the pages, so the
// two seam pages it hands the browser are served from here: the dispatch page
// the picker's form submission lands on, and the page the broker sends its own
// never-redirect failures to.
export enum SeamPage {
  Dispatch = 'dispatch',
  Error = 'error',
}

export const DISPATCH_PATH = '/oidc/dispatch';
export const ERROR_PATH = '/oidc/error';

const LOGIN_PATH = '/oidc/login';
const CONTINUE_PATH = '/oidc/continue';
const REQUEST_ID_PARAM = 'request_id';
const HINT_PARAM = 'username';
const SERVER_ERROR_PARAM = 'server';
const BROWSER_MISMATCH_PARAM = 'browser';
const RATE_LIMITED_PARAM = 'rate';

// The dispatch page writes this into its own history entry before it
// forwards, so a later visit to that entry can tell the flow already left.
const FORWARDED_STATE = 'oidc-dispatch-forwarded';

// The broker parses this id as a UUID before it ever reaches a Location, so an
// id that does not round-trip that shape did not come from the broker.
export const REQUEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MAX_HINT_BYTES = 254;

// seamPageFor names the seam page a path serves, or null when the portal renders
// instead. /oidc/login without a flow id is a dead link and gets the error page:
// the portal would not run under the strict policy nginx sets on that path.
export const seamPageFor = (pathname: string, search: string): SeamPage | null => {
  switch (pathname.replace(/\/+$/, '')) {
    case DISPATCH_PATH:
      return SeamPage.Dispatch;
    case ERROR_PATH:
      return SeamPage.Error;
    case LOGIN_PATH:
      return REQUEST_ID_PATTERN.test(new URLSearchParams(search).get(REQUEST_ID_PARAM) ?? '') ? null : SeamPage.Error;
    default:
      return null;
  }
};

// A prefill hint is bounded the same way the broker bounds it: non-empty, at
// most 254 bytes, and free of control characters. The bound is in bytes, not
// UTF-16 code units, which would admit a longer string.
const validLoginHint = (hint: string): boolean => {
  if (hint.length === 0) {
    return false;
  }
  let bytes = 0;
  for (const ch of hint) {
    const cp = ch.codePointAt(0) as number;
    if (cp < 0x20 || cp === 0x7f) {
      return false;
    }
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    if (bytes > MAX_HINT_BYTES) {
      return false;
    }
  }
  return true;
};

// continueURL is the one value on the dispatch page that moves the browser, so
// it is rebuilt from validated parts onto a fixed same-origin path rather than
// carried over from the query. A flow id that is not a UUID yields null and the
// caller shows the error page instead of forwarding.
export const continueURL = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const requestID = params.get(REQUEST_ID_PARAM) ?? '';
  if (!REQUEST_ID_PATTERN.test(requestID)) {
    return null;
  }

  const target = new URLSearchParams({ [REQUEST_ID_PARAM]: requestID });
  const hint = params.get(HINT_PARAM);
  if (hint !== null && validLoginHint(hint)) {
    target.set(HINT_PARAM, hint);
  }
  return `${CONTINUE_PATH}?${target.toString()}`;
};

// markForwarded records on the current history entry that this dispatch page
// sent its flow on, keeping the entry's URL.
export const markForwarded = (history: History): void => history.replaceState(FORWARDED_STATE, '');

// wasForwarded reports whether the current history entry is a dispatch page
// that already sent its flow on.
export const wasForwarded = (history: History): boolean => history.state === FORWARDED_STATE;

// brokerMarked reports whether the broker set its marker param to 1.
const brokerMarked = (search: string, param: string): boolean => new URLSearchParams(search).get(param) === '1';

// isServerError splits the broker's temporary failures from its rejected-flow
// failures, which read as the same page with different copy.
export const isServerError = (search: string): boolean => brokerMarked(search, SERVER_ERROR_PARAM);

// isBrowserMismatch marks a flow continued in a browser that did not start it.
export const isBrowserMismatch = (search: string): boolean => brokerMarked(search, BROWSER_MISMATCH_PARAM);

// isRateLimited marks a step a broker rate limit turned away.
export const isRateLimited = (search: string): boolean => brokerMarked(search, RATE_LIMITED_PARAM);
